import { keepAlive, refreshToken, revokeToken } from "./api"
import { getCookie, deleteCookie } from "./cookies"
import PubSub from "./pubsub"

const pubSub = new PubSub<void>()

/**
 * Extend the length of a token, or if extension limit reached refresh access
 * token.
 *
 * The token will be refreshed if the TTL of the existing token is below the
 * refresh threshold value. When the access token is refreshed, then the new
 * access token gets stored in session storage.
 *
 * | Env Variable                                  | Description                                                                                 |
 * | --------------------------------------------- | ------------------------------------------------------------------------------------------- |
 * | <code>REACT_APP_SERVER_BASE</code>            | Overrides the default `baseUrl`                                                             |
 * | <code>REACT_APP_REFRESH_THRESHOLD_MINS</code> | Overrides the default `refreshThresholdSeconds`; however, value is in minutes, not seconds. |
 *
 * @example
 *
 * ```typescript
 * import { extendOrRefreshAccessToken } from "@hbo/ui-react-helpers"
 *
 * const succeed = await extendOrRefreshAccessToken({})
 * ```
 *
 * @param {object} options Configuration options
 * @param {string | null} [options.baseUrl=http://localhost:8080] Base Url of
 *   API server. Default is `http://localhost:8080`
 * @param {number | null} [options.refreshThresholdSeconds=6] Time remaining (in
 *   seconds) where token can no longer be extended and must be refreshed.
 *   Default is `6`
 * @returns {Promise<boolean>} Whether the token was succesfully extended or
 *   refreshed
 */
export async function extendOrRefreshAccessToken(options: {
    baseUrl?: string
    refreshThresholdSeconds?: number
}): Promise<boolean> {
    let { baseUrl, refreshThresholdSeconds } = options

    baseUrl = baseUrl ?? FALLBACK_BASE_URL
    refreshThresholdSeconds =
        refreshThresholdSeconds ?? DEFAULT_REFRESH_THRESHOLD_SECONDS

    const accessToken = getAccessToken()

    try {
        const timeToLive = await keepAlive(accessToken, baseUrl)

        if (timeToLive < refreshThresholdSeconds * 1000) {
            tokenStore(await refreshToken(accessToken, baseUrl))
        }
        return true
    } catch (ex) {
        tokenClear()
        pubSub.publish("token-death")
        return false
    }
}

/**
 * Get access token.
 *
 * The following steps take place to try to retrieve access token:
 *
 * 1. Session storage for token, returning token if found
 * 2. Check cookies for token, returning token if found
 *
 * @example
 *
 * ```typescript
 * import { getAccessToken } from "@hbo/ui-react-helpers"
 *
 * const accessToken = getAccessToken()
 * ```
 *
 * @returns {string} Access token. If no token found then will return `""`
 */
export function getAccessToken(): string {
    let token = window.sessionStorage.getItem(SESSION_STORAGE_KEY)

    if (!token) token = getCookie(COOKIE_KEY) ?? ""

    return token
}

/**
 * Revoke access token and remove from session.
 *
 * Should call to revoke token fail, the access token will still be removed from
 * session.
 *
 * | Env Variable                       | Description                     |
 * | ---------------------------------- | ------------------------------- |
 * | <code>REACT_APP_SERVER_BASE</code> | Overrides the default `baseUrl` |
 *
 * @example
 *
 * ```typescript
 * import { revokeAccessToken } from "@hbo/ui-react-helpers"
 *
 * const succeed = await revokeAccessToken({})
 * ```
 *
 * @param {object} options Configuration options
 * @param {string | null} [options.baseUrl=http://localhost:8080] Base Url of
 *   API server. Default is `http://localhost:8080`
 * @returns {Promise<boolean>} Whether the token was succesfully revoked
 */
export async function revokeAccessToken(options: {
    baseUrl?: string
}): Promise<boolean> {
    let { baseUrl } = options
    baseUrl = baseUrl ?? FALLBACK_BASE_URL

    const accessToken = getAccessToken()
    tokenClear()

    let isSuccessful = true
    try {
        await revokeToken(accessToken, baseUrl)
    } catch {
        isSuccessful = false
    }
    pubSub.publish("token-death")

    return isSuccessful
}

/**
 * Create subscription to token death messages.
 *
 * The token death occurs when:
 *
 * - Failure occurs extending token
 * - Failure occurs refreshing token
 * - Call made to `revokeAccessToken`
 *
 * @example
 *
 * ```typescript
 * import { subscribeToTokenDeath } from "@hbo/ui-react-helpers"
 *
 * const unsubscribe = await subscribeToTokenDeath(() => {
 *     console.log("Token death occured")
 * })
 *
 * // At some point unsubscribe to event.
 * setTimeout(() => {
 *     unsubscribe()
 * }, 1000)
 * ```
 *
 * @param {Function} callback Function to be called when token death occurs
 * @returns {Function} Returns a function that will end the subscription
 */
export function subscribeToTokenDeath(callback: () => void): () => void {
    return pubSub.subscribe("token-death", callback)
}

/**
 * Clears token.
 *
 * Will remove access token from session storage and cookies, then publish token
 * death message.
 *
 * @ignore
 */
function tokenClear() {
    window.sessionStorage.removeItem(SESSION_STORAGE_KEY)
    deleteCookie(COOKIE_KEY)
}

/**
 * Updates session storage, so token is available to be read on page refresh.
 *
 * @ignore
 * @param {string} token New access token to store
 */
function tokenStore(token: string) {
    window.sessionStorage.setItem(SESSION_STORAGE_KEY, token)
}

export const COOKIE_KEY = "access_token"
export const SESSION_STORAGE_KEY = "access_token"

const DEFAULT_REFRESH_THRESHOLD_SECONDS =
    Number(process.env.REACT_APP_REFRESH_THRESHOLD_MINS) * 60 || 6 * 60
const FALLBACK_BASE_URL =
    process.env.REACT_APP_SERVER_BASE || "http://localhost:8080"
